/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    ExceptionCustomFieldsService,
    ExceptionsService,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    ICustomField,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { ExceptionStore } from './exception.store';
import {
    ExceptionCreate,
    ExceptionRead,
    ExceptionSearchParams,
} from '../../entities/exception.dto';
import { Operation } from 'fast-json-patch';

@Injectable()
export class ExceptionDataService extends AbstractDataService<
    ExceptionRead,
    ExceptionCreate
> {
    private customFieldsService = inject(ExceptionCustomFieldsService);
    protected EntityName = PFWmsEntities.Exception;
    constructor(
        private service: ExceptionsService,
        store: ExceptionStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(body: ExceptionCreate): Observable<ExceptionRead> {
        return this.service.exceptionEntityCreate({
            exceptionViewModelCreateBody: body,
        }) as Observable<ExceptionRead>;
    }
    protected deleteObs$(id: string): Observable<ExceptionRead> {
        return this.service.exceptionEntityDelete({
            id,
        }) as Observable<ExceptionRead>;
    }
    protected undoDeleteObs$(id: string): Observable<ExceptionRead> {
        return this.service.exceptionEntityUndoDelete({
            id,
        }) as Observable<ExceptionRead>;
    }
    protected getObs$(id: string): Observable<ExceptionRead> {
        return this.service.exceptionEntityReadById({
            id,
        }) as Observable<ExceptionRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<ExceptionRead> {
        return this.service.exceptionEntityUpdatePatch({
            id,
            operation: operations,
        }) as Observable<ExceptionRead>;
    }
    protected searchObs$(
        searchParams: ExceptionSearchParams
    ): Observable<IPagedResult<ExceptionRead>> {
        return this.service.exceptionEntitySearch(searchParams) as Observable<
            IPagedResult<ExceptionRead>
        >;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<ExceptionRead>>> {
        return this.service.exceptionEntityGetChanges(
            searchParams
        ) as Observable<IPagedResult<EntityChangeTrackingDto<ExceptionRead>>>;
    }

    protected override createCustomFieldObs$(
        entityId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.exceptionEntityCreateCustomField({
            entityId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override updateCustomFieldObs$(
        entityId: string,
        customFieldId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.exceptionEntityCustomFieldsUpdatePut({
            entityId,
            id: customFieldId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override deleteCustomFieldObs$(
        entityId: string,
        customFieldId: string
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.exceptionEntityDeleteCustomField({
            entityId,
            id: customFieldId,
        }) as Observable<ICustomField & { id: string }>;
    }
}
